import { getFlags } from "@/lib/flags";
import { functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import { logLibraryEvent } from "./libraryAnalytics";

import { accountStorage } from "./accountStorage";

const PROGRESS = "axoris.mock.lib.progress";
const BOOKMARKS = "axoris.mock.lib.bookmarks";
const HIGHLIGHTS = "axoris.mock.lib.highlights";

const failMode = () => new URLSearchParams(window.location.search).get("fail");

export async function getCatalog() {
  const cache = accountStorage();
  const read = cache.read;
  if (failMode() === "read") return { error: "read" };
  try {
    const fn = httpsCallable(functions, "getCatalog");
    const res = await fn({});
    if (!cache.isCurrent()) return { error: "read" };
    if (res.data?.ok) {
      let progress = read(PROGRESS, {});
      try {
        const progFn = httpsCallable(functions, "getLibraryProgress");
        const progRes = await progFn({});
        if (progRes.data?.ok && progRes.data.progress) {
          progress = progRes.data.progress;
          cache.write(PROGRESS, progress);
        }
      } catch {}

      if (!cache.isCurrent()) return { error: "read" };
      return {
        books: res.data.books,
        locked: res.data.locked || getFlags().locked,
        progress,
      };
    }
    return { error: "read" };
  } catch {
    return { error: "read" };
  }
}

export async function getBook(id) {
  const cache = accountStorage();
  const read = cache.read;
  if (failMode() === "read") return { error: "read" };
  try {
    const fn = httpsCallable(functions, "getBook");
    const res = await fn({ bookId: id });
    if (!cache.isCurrent()) return { error: "read" };
    if (res.data?.ok) {
      let progress = read(PROGRESS, {})[id];
      let bookmark = read(BOOKMARKS, {})[id] ?? null;

      try {
        const progFn = httpsCallable(functions, "getLibraryProgress");
        const progRes = await progFn({});
        if (progRes.data?.ok && progRes.data.progress) {
          const p = progRes.data.progress;
          cache.write(PROGRESS, p);
          progress = p[id] ?? null;
        }
      } catch {}

      try {
        const bmFn = httpsCallable(functions, "getBookmarks");
        const bmRes = await bmFn({});
        if (bmRes.data?.ok && bmRes.data.bookmarks) {
          const bms = bmRes.data.bookmarks;
          cache.write(BOOKMARKS, bms);
          bookmark = bms[id] ?? null;
        }
      } catch {}

      if (!cache.isCurrent()) return { error: "read" };
      if (res.data?.book) {
        void logLibraryEvent("book_view", {
          bookId: res.data.book.id,
          origin: res.data.book.origin,
          rightsClass: res.data.book.rightsClass,
        });
      }
      return {
        book: res.data.book,
        locked: res.data.locked || getFlags().locked,
        progress: progress ?? null,
        bookmark: bookmark ?? null,
      };
    }
    return { error: res.data?.error || "missing" };
  } catch (err) {
    if (err?.code === "functions/not-found") return { error: "missing" };
    return { error: "read" };
  }
}

export async function getChapter(bookId, chapterId) {
  const cache = accountStorage();
  const read = cache.read;
  if (getFlags().locked) return { error: "locked" };
  if (failMode() === "chapter") return { error: "fail" };
  try {
    const fn = httpsCallable(functions, "getChapterUrl");
    const res = await fn({ bookId, chapterId });
    if (!res.data?.ok || !res.data?.url) {
      return { error: "fail" };
    }

    // Gunakan URL sekali dalam memori via fetch, buang setelah memuat
    const signedUrl = res.data.url;
    const response = await fetch(signedUrl);
    if (!response.ok) {
      return { error: "fail" };
    }

    const markdownText = await response.text();
    // Urai paragraf markdown (pisah berdasarkan baris ganda, buang header H1 jika ada)
    const rawBlocks = markdownText
      .split(/\n\s*\n/)
      .map((b) => b.trim())
      .filter(Boolean)
      .filter((b) => !b.startsWith("# "));
    const blocks = rawBlocks.length > 0 ? rawBlocks : [markdownText];

    let progress = read(PROGRESS, {})[bookId] ?? null;
    let bookmark = read(BOOKMARKS, {})[bookId] ?? null;

    try {
      const bmFn = httpsCallable(functions, "getBookmarks");
      const bmRes = await bmFn({});
      if (bmRes.data?.ok && bmRes.data.bookmarks) {
        const bms = bmRes.data.bookmarks;
        cache.write(BOOKMARKS, bms);
        bookmark = bms[bookId] ?? null;
      }
    } catch {}

    if (!cache.isCurrent()) return { error: "read" };
    void logLibraryEvent("library_chapter_opened", { bookId, chapterId });
    return {
      book: res.data.book,
      chapter: {
        id: res.data.chapter.id,
        title: res.data.chapter.title,
        minutes: res.data.chapter.minutes,
        body: {
          id: blocks,
          en: blocks,
        },
      },
      prev: res.data.prev,
      next: res.data.next,
      progress,
      bookmark,
    };
  } catch (err) {
    if (
      err?.code === "functions/permission-denied" ||
      err?.message?.includes("Entitlement")
    ) {
      return { error: "locked" };
    }
    if (err?.code === "functions/not-found") {
      return { error: "missing" };
    }
    return { error: "fail" };
  }
}

const pendingWrites = new Map();
function orderedWrite(key, task) {
  const next = (pendingWrites.get(key) || Promise.resolve()).then(task);
  pendingWrites.set(key, next);
  next.finally(() => { if (pendingWrites.get(key) === next) pendingWrites.delete(key); });
  return next;
}

// Completion-aware additions; existing signatures remain available below.
export function persistProgress(bookId, chapterId, blockIndex) {
  const cache = accountStorage();
  return orderedWrite(`${cache.uid}:progress:${bookId}`, async () => {
    if (!cache.isCurrent()) return { error: "write" };
    try {
      const res = await httpsCallable(functions, "saveProgress")({ bookId, chapterId, blockIndex });
      if (!cache.isCurrent() || !res.data?.ok || !res.data.progress) return { error: "write" };
      const p = cache.read(PROGRESS, {});
      p[bookId] = { ...res.data.progress, updatedAt: new Date().toISOString() };
      cache.write(PROGRESS, p);
      return { progress: p[bookId] };
    } catch { return { error: "write" }; }
  });
}

export function persistBookmark(bookId, chapterId) {
  const cache = accountStorage();
  return orderedWrite(`${cache.uid}:bookmark:${bookId}`, async () => {
    if (!cache.isCurrent()) return { error: "write" };
    try {
      const res = await httpsCallable(functions, "setBookmark")({ bookId, chapterId });
      if (!cache.isCurrent() || !res.data?.ok || typeof res.data.bookmarked !== "boolean" ||
          (res.data.bookmarked && !res.data.bookmark)) return { error: "write" };
      const m = cache.read(BOOKMARKS, {});
      if (res.data.bookmarked) m[bookId] = res.data.bookmark;
      else delete m[bookId];
      cache.write(BOOKMARKS, m);
      return { bookmark: res.data.bookmarked ? res.data.bookmark : null };
    } catch { return { error: "write" }; }
  });
}

export function saveProgress(bookId, chapterId, blockIndex) {
  void persistProgress(bookId, chapterId, blockIndex);
}

export function toggleBookmark(bookId, chapterId) {
  const confirmed = accountStorage().read(BOOKMARKS, {})[bookId] ?? null;
  void persistBookmark(bookId, chapterId);
  return confirmed;
}

export const listHighlights = (bookId, chapterId) =>
  accountStorage().read(HIGHLIGHTS, []).filter((h) => (!bookId || h.bookId === bookId) && (!chapterId || h.chapterId === chapterId));

export async function fetchHighlights(bookId, chapterId) {
  const cache = accountStorage();
  const read = cache.read;
  try {
    const fn = httpsCallable(functions, "listHighlights");
    const payload = {};
    if (bookId !== undefined) payload.bookId = bookId;
    if (chapterId !== undefined) payload.chapterId = chapterId;
    const res = await fn(payload);
    if (!cache.isCurrent()) return [];
    if (res.data?.ok && Array.isArray(res.data?.highlights)) {
      cache.write(HIGHLIGHTS, res.data.highlights);
      return read(HIGHLIGHTS, []).filter((h) => (!bookId || h.bookId === bookId) && (!chapterId || h.chapterId === chapterId));
    }
  } catch (err) {
    console.error("fetchHighlights failed:", err);
  }
  return read(HIGHLIGHTS, []).filter((h) => (!bookId || h.bookId === bookId) && (!chapterId || h.chapterId === chapterId));
}

export async function saveHighlight({ bookId, chapterId, text, color, remark }) {
  const cache = accountStorage();
  const read = cache.read;
  if (failMode() === "write") return { error: "write" };
  const t = text.trim();
  const r = (remark || "").trim();
  if (t.length < 8 || t.length > 280 || r.length > 280 || !["teal", "gold"].includes(color)) {
    return { error: "invalid" };
  }

  try {
    const fn = httpsCallable(functions, "saveHighlight");
    const res = await fn({ bookId, chapterId, text: t, color, remark: r });
    if (!cache.isCurrent()) return { error: "write" };
    if (res.data?.ok && res.data.highlight) {
      const h = res.data.highlight;
      void logLibraryEvent("highlight_created", { color: h.color });
      const list = read(HIGHLIGHTS, []);
      const idx = list.findIndex((x) => x.id === h.id);
      if (idx >= 0) list[idx] = h;
      else list.push(h);
      cache.write(HIGHLIGHTS, list);
      return { highlight: h };
    }
    return { error: "write" };
  } catch (err) {
    if (err?.message?.includes("Pilih satu frasa") || err?.code === "functions/invalid-argument") {
      return { error: "invalid" };
    }
    return { error: "write" };
  }
}

export async function saveHighlightToJournal(highlightId, decisionId) {
  const cache = accountStorage();
  const read = cache.read;
  try {
    const fn = httpsCallable(functions, "saveHighlightToJournal");
    const res = await fn({ highlightId, decisionId });
    if (!cache.isCurrent()) return { error: "write" };
    if (res.data?.ok && res.data.highlight) {
      const h = res.data.highlight;
      const list = read(HIGHLIGHTS, []);
      const idx = list.findIndex((x) => x.id === highlightId);
      if (idx >= 0) {
        list[idx] = { ...list[idx], decisionId: h.decisionId, itemId: h.itemId };
      }
      cache.write(HIGHLIGHTS, list);
      return { highlight: h, item: res.data.item };
    }
    return { error: "write" };
  } catch (err) {
    if (err?.code === "functions/permission-denied") {
      return { error: "permission-denied" };
    }
    return { error: "write" };
  }
}

export async function getRelatedChapters(decisionId) {
  const cache = accountStorage();
  try {
    const fn = httpsCallable(functions, "getRelatedChapters");
    const res = await fn({ decisionId });
    if (!cache.isCurrent()) return { error: "read" };
    if (res.data?.ok) {
      return {
        chapters: res.data.chapters || [],
        locked: !!res.data.locked,
      };
    }
    return { error: "read" };
  } catch (err) {
    console.error("getRelatedChapters error:", err);
    return { error: "read" };
  }
}

